import { List, Map } from 'immutable';

import { getCombinationsErrors, getOptionsErrors, getVariantNameError, getVariantsErrors } from '../validations/variations';
import { findAnalogueScale, getAvailable } from '../taxonomy';
import { updateCombinations } from './combinations';
import { updateInfluence } from './influence';
import { getLowestId } from './getLowestId';
import { setInvalid } from './errors';
import { getSize } from '../iterable/getSize';

import { ALL, COMBINATIONS, DELETED, ERRORS, INFLUENCE, RETAIN, VARIANTS } from '../../constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { ERROR, VALUE } from '../../constants/product';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function updateVariations({
  product,
  shopData,
  validateCombinations = false,
  validateOptions = false,
  variants,
  variations,
}) {
  const channel = product.get('channel');
  let updatedVariations = variations || product.get(FORM.VARIATIONS);

  if (variants) {
    const combinations = updateCombinations({
      channel,
      inventory: updatedVariations.has(RETAIN)
        ? undefined
        : product.get(FORM.INVENTORY),
      shipping: updatedVariations.has(RETAIN)
        ? undefined
        : product.get(FORM.SHIPPING),
      variants,
      variations: updatedVariations,
    });

    updatedVariations = updatedVariations.set(COMBINATIONS, combinations).set(VARIANTS, variants);
  }

  updatedVariations = updatedVariations.set(ERROR, getVariantsErrors({ channel, variations: updatedVariations }));

  if (validateOptions) {
    updatedVariations = updatedVariations.set(ERROR, getOptionsErrors({ channel, variations: updatedVariations }));
  }

  if (validateCombinations) {
    updatedVariations = updatedVariations.set(ERROR,
      getCombinationsErrors({ channel, shopData, variations: updatedVariations })
    );
  }

  return setInvalid(updatedVariations);
}

export function addVariant({ affecting, product, shopData, taxonomyData, variant }) {
  const channel = product.get('channel');
  const variations = product.get(FORM.VARIATIONS);
  const variants = variations.get(VARIANTS);
  const variantsSize = getSize(variants);
  const id = Math.min(-variantsSize, variants.reduce(getLowestId, 0)) - 1;

  switch (channel) {
    case EBAY: {
      if (affecting && variant.get('name') in affecting) {
        const variantName = variant.get('name');
        const affected = affecting[variantName];

        return updateVariations({
          product,
          shopData,
          variants: variants.push(
            variant.set('id', id).set('options', EMPTY.LIST),
            Map({ id: id - 1, name: affected, options: EMPTY.LIST }),
          ),
          variations: updateInfluence({
            affects: { affected, variantIndex: variantsSize, variantName },
            product,
            taxonomyData,
            variations: variations
              .setIn([ERROR, VARIANTS, variantsSize], EMPTY.MAP)
              .setIn([ERROR, VARIANTS, variantsSize + 1], EMPTY.MAP),
          }),
        });
      }

      return updateVariations({
        product,
        shopData,
        variants: variants.push(variant.set('id', id).set('options', EMPTY.LIST)),
        variations: variations.setIn([ERROR, VARIANTS, variantsSize], EMPTY.MAP),
      });
    }

    case ETSY: {
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
      const { availableScales } = getAvailable({ category, channel, property: variant.get('property') });

      if (getSize(availableScales) > 0) {
        return updateVariations({
          product,
          shopData,
          variants: variants.push(variant.set('id', id).set('options', EMPTY.LIST).set('scale', 0)),
          variations: variations.setIn([ERROR, VARIANTS, variantsSize], Map({ scale: ERRORS.VARIANTS.SCALE })),
        });
      }

      return updateVariations({
        product,
        shopData,
        variants: variants.push(variant.set('id', id).set('options', EMPTY.LIST)),
        variations: variations.setIn([ERROR, VARIANTS, variantsSize], EMPTY.MAP),
      });
    }

    default: {
      return updateVariations({
        product,
        shopData,
        variants: variants.push(variant.set('id', id).set('options', EMPTY.LIST)),
        variations: variations.setIn([ERROR, VARIANTS, variantsSize], EMPTY.MAP),
      });
    }
  }
}

export function deleteVariant({ product, shopData, variantIndex }) {
  const channel = product.get('channel');
  const variations = product.get(FORM.VARIATIONS);

  function getErrors({ indexes }) {
    function reduceErrors(result, value, index) {
      return index < indexes.first()
        ? result.set(index, value)
        : index > indexes.last()
          ? result.set(index - getSize(indexes), value)
          : result;
    }

    return variations.getIn([ERRORS, VARIANTS], EMPTY.MAP).reduce(reduceErrors, EMPTY.MAP);
  }

  function updatePropertyId(leftVariant, leftVariantIndex) {
    return CUSTOM_PROPERTY_IDS[channel].includes(leftVariant.get('property'))
      ? leftVariant.set('property', CUSTOM_PROPERTY_IDS[channel][leftVariantIndex])
      : leftVariant;
  }

  function reduceInfluence(influence, values, attribute) {
    function reduceValues(reducedValues, value, index) {
      if (index < variantIndex) {
        return reducedValues.set(index, value);
      }

      if (index > variantIndex) {
        return reducedValues.set(index - 1, value);
      }

      return reducedValues;
    }

    return influence.set(attribute, values.reduce(reduceValues, EMPTY.MAP));
  }

  function reduceVariantsToNameErrors(result, variant, index) {
    const error = getVariantNameError({
      channel,
      name: variant.get('name'),
      variants: result.variants,
      variantIndex: index,
    });

    if (error) {
      result.errors = result.errors.setIn([index, 'name'], error);
    } else {
      result.errors = result.errors.deleteIn([index, 'name']);
    }

    return result;
  }

  switch (channel) {
    case EBAY: {
      const indexes = variations.hasIn([INFLUENCE, variantIndex, 'affectedBy'])
        ? List([variations.getIn([INFLUENCE, variantIndex, 'affectedBy']), variantIndex]).sort()
        : variations.hasIn([INFLUENCE, variantIndex, 'affecting'])
          ? List([variantIndex, variations.getIn([INFLUENCE, variantIndex, 'affecting'])]).sort()
          : List([variantIndex]);

      const variants = variations.get(VARIANTS).splice(indexes.first(), getSize(indexes));
      const influence = getSize(indexes) > 1
        ? variations.get(INFLUENCE).deleteAll(indexes)
        : variations.get(INFLUENCE);

      const { errors } = variants.reduce(
        reduceVariantsToNameErrors,
        { errors: getErrors({ indexes }), variants },
      );

      return updateVariations({
        product,
        shopData,
        validateCombinations: true,
        validateOptions: true,
        variants,
        variations: variations
          .set(INFLUENCE, influence)
          .setIn([ERROR, VARIANTS], errors),
      });
    }

    case ETSY: {
      const variants = variations.get(VARIANTS).splice(variantIndex, 1).map(updatePropertyId);
      const influence = variations.get(INFLUENCE).delete(ALL).reduce(reduceInfluence, Map({ [ALL]: false }));
      const errors = getErrors({ indexes: List([variantIndex]) });

      return updateVariations({
        product,
        shopData,
        validateCombinations: true,
        validateOptions: true,
        variants,
        variations: variations
          .setIn([ERROR, VARIANTS], errors)
          .set(INFLUENCE, influence),
      });
    }

    case FAIRE: {
      const variants = variations.get(VARIANTS).splice(variantIndex, 1);
      const { errors } = variants.reduce(
        reduceVariantsToNameErrors,
        { errors: getErrors({ indexes: List([variantIndex]) }), variants },
      );

      return updateVariations({
        product,
        shopData,
        validateCombinations: true,
        validateOptions: true,
        variants,
        variations: variations.setIn([ERROR, VARIANTS], errors),
      });
    }

    case SHOPIFY: {
      const variants = variations.get(VARIANTS).splice(variantIndex, 1);
      const { errors } = variants.reduce(
        reduceVariantsToNameErrors,
        { errors: getErrors({ indexes: List([variantIndex]) }), variants },
      );

      return updateVariations({
        product,
        shopData,
        validateCombinations: true,
        validateOptions: true,
        variants,
        variations: variations.delete(DELETED).setIn([ERROR, VARIANTS], errors),
      });
    }

    default: {
      const errors = getErrors({ indexes: List([variantIndex]) });
      return updateVariations({
        product,
        shopData,
        validateCombinations: true,
        validateOptions: true,
        variants: variations.get(VARIANTS).splice(variantIndex, 1),
        variations: variations.setIn([ERROR, VARIANTS], errors),
      });
    }
  }
}

export function updateVariant({ product, shopData, variant: sourceVariant, variantIndex }) {
  const channel = product.get('channel');
  let variations = product.get(FORM.VARIATIONS);
  let variant = sourceVariant;
  const variants = variations.get(VARIANTS);

  function replaceOptionsWithAvailable(options, availableOptions) {
    function mapOptions(option) {
      function findSameOption({ name }) {
        return name === option.get('value');
      }

      return option.set('valueId', availableOptions.find(findSameOption)?.id || null);
    }

    return options.map(mapOptions);
  }

  function setVariantNameError(updatedVariants) {
    return function reduce(errors, updatedVariant, index) {
      const error = getVariantNameError({
        channel,
        name: updatedVariant.get('name'),
        variants: updatedVariants,
        variantIndex: index,
      });

      return error
        ? errors.setIn([index, 'name'], error)
        : errors.deleteIn([index, 'name']);
    };
  }

  if (variant.get('property') !== variants.getIn([variantIndex, 'property'])) {
    const category = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
    const property = variant.get('property');
    const scale = variant.get('scale');
    const { availableScales, availableOptions } = getAvailable({ category, channel, property });

    if (variant.get('scale') === variants.getIn([variantIndex, 'scale'])) {
      if (getSize(availableScales)) {
        const analogueScale = scale && availableScales.find(findAnalogueScale({ channel, scale }));

        if (analogueScale) {
          variations = variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);
          variant = variant.set('scale', analogueScale.id);
        } else {
          variations = variations.setIn([ERROR, VARIANTS, variantIndex, 'scale'], ERRORS.VARIANTS.SCALE);
          variant = variant.set('scale', 0);
        }
      } else {
        variations = variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);
        variant = variant.delete('scale');
      }
    }

    variant = variant.set('options', replaceOptionsWithAvailable(variant.get('options'), availableOptions));

    const updatedVariants = variants.set(variantIndex, variant);
    variations = variations.setIn([ERROR, VARIANTS], updatedVariants.reduce(
      setVariantNameError(updatedVariants),
      variations.getIn([ERROR, VARIANTS])
    ));
  } else if (variant.get('name') !== variants.getIn([variantIndex, 'name'])) {
    const updatedVariants = variants.set(variantIndex, variant);

    variations = variations.setIn([ERROR, VARIANTS], updatedVariants.reduce(
      setVariantNameError(updatedVariants),
      variations.getIn([ERROR, VARIANTS])
    ));
  }

  if (variant.get('scale') !== variants.getIn([variantIndex, 'scale'])) {
    const category = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
    const property = variant.get('property');
    const scale = variant.get('scale');
    const { availableScales, availableOptions } = getAvailable({ category, channel, property, scale });

    variations = getSize(availableScales) > 0 && !availableScales.find(findAnalogueScale({ channel, scale }))
      ? variations.setIn([ERROR, VARIANTS, variantIndex, 'scale'], ERRORS.VARIANTS.SCALE)
      : variations.deleteIn([ERROR, VARIANTS, variantIndex, 'scale']);

    variant = variant.set('options', replaceOptionsWithAvailable(variant.get('options'), availableOptions));
  }

  return updateVariations({ product, shopData, variants: variants.set(variantIndex, variant), variations });
}

export function variantHasPhotos({ channel, variant }) {
  function findOptionWithPhotos(option) {
    switch (channel) {
      case EBAY: {
        return getSize(option.get('photos')) > 0;
      }

      case ETSY: {
        return option.get('imageHash');
      }

      default: {
        return false;
      }
    }
  }

  return variant.get('options', EMPTY.LIST).findIndex(findOptionWithPhotos) !== -1;
}
